// Packaging --- a packaging part (Type Packaging: part_type 'packaging') in the Parts library and the item panel: its
// Category (box, sleeve, bag…: item.pack_type, set by the panel's Category field) and how it's printed (pack_print), and
// the print files those ask for (packagingOf() in shared/projects.ts), each against the released revision, which is what
// a vendor gets on a PO. Dropping a file on a file's row starts the spec if there's none, makes that file's slot and
// uploads it. NewPackagingDialog asks the same questions when one is made; NeedChips is a part's files as chips (the
// Parts library and the project's Files tab).
// Product labels --- a label or sticker printed as "HHL product label" has no files: HHL Ops makes each product's label
// (shared/product-labels.ts, drawn by the server), so its section sets the size and the line every label carries, lists
// the products its BOMs put it on with what's off about each (at its size: a barcode too narrow to scan too), and
// previews the chosen one's label at its shape. The size says which sent POs' vendors it changes the labels of (they're
// made when downloaded), and print files left in its released revision from before are said to go to no one now.
import { AlertTriangle, Download, ExternalLink, Eye, Plus, Rocket, Tag, Upload as UploadIcon } from "lucide-react";
import { type DragEvent, type ReactNode, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { FileVersion, Item, SpecFile } from "../../../shared/db.gen.ts";
import { labelPresets } from "../../../shared/label-size.ts";
import { isCurrent } from "../../../shared/part-groups.ts"; // Part groups ---
import { PART_PREFIXES, skuStem } from "../../../shared/part-sku.ts";
import { PACK_CATEGORY_LABELS } from "../../../shared/part-type.ts";
import { DEFAULT_LABEL_SIZE, labelContent, labelSize, productsLabelled, sizeText } from "../../../shared/product-labels.ts";
import {
  type FileNeed,
  type filesOf,
  PACK_PRINT_OPTIONS,
  PACK_TYPES,
  type PackPrint,
  type PackType,
  packagingOf,
  printLabel,
} from "../../../shared/projects.ts";
import { useBomStructure } from "../lib/boms.ts";
import { ago } from "../lib/format.ts";
import { can, mutate, mutateWithResult } from "../lib/mutate.ts";
import { Link } from "../lib/router.tsx";
import { useRows } from "../lib/sync.ts";
import { Badge, Button, cx, Input, Select } from "../ui/controls.tsx";
import { DialogLayer } from "../ui/DialogLayer.tsx";
import { useFileDrop } from "../ui/FileDrop.tsx";
import { LabelSizeFields } from "../ui/LabelSizeFields.tsx";
import { PaneLink, usePaneOpen } from "../ui/SidePane.tsx";
import { restOf, SkuInput } from "../ui/SkuInput.tsx";
import { toast } from "../ui/toast.tsx";
import { ShareChoice, usePartGroups } from "./PartGroups.tsx"; // Part groups ---
import { leftoverFiles, useSentPosLabelling } from "./po/packaging.ts";
import { bytes, downloadUrl, needSlot, specFor, statusOf, type Upload, uploadNeed, useSpecIndex, useUploads, viewable } from "./specs/lib.ts";
import { ReleaseDialog } from "./specs/SpecRevisions.tsx";
import { itemPath } from "./specs/SpecWorkspace.tsx";

const isPackType = (t: string | null | undefined): t is PackType => (PACK_TYPES as readonly string[]).includes(t ?? "");
/** What setting a packaging part's Category writes: printing the new Category can't have goes back to the usual for it. */
export function packCategoryPatch(item: Pick<Item, "pack_print">, t: PackType | ""): Pick<Item, "pack_type" | "pack_print"> {
  const keep = !!item.pack_print && !!t && PACK_PRINT_OPTIONS[t].includes(item.pack_print as PackPrint);
  return { pack_type: t, pack_print: keep ? item.pack_print : "" };
}
/** The Spec section of the same panel, where the spec's text and its Release button are. */
const toSpec = () => document.getElementById("sec-spec")?.scrollIntoView({ block: "start", behavior: "smooth" });
/** The parts library's labels, to find the one that goes with a bag or box. */
export const LABELS_PATH = "/parts?kind=packaging&pack=label";

/**
 * Product labels --- the PDFs server/src/product-labels.ts makes (inline unless `download`). A label part's: a page per
 * product it labels, or only `item`'s (a sample page while it labels none); `v` changes the address when the label or the
 * product changes, so a preview reloads.
 */
export const labelPartUrl = (partId: string, o: { item?: string; download?: boolean; v?: string } = {}) =>
  `/api/labels/label-part/${partId}${query({ item: o.item, download: o.download ? "1" : undefined, v: o.v })}`;
/** A product's labels: every HHL product label its BOM puts in it, or only `label`'s. */
export const productLabelsUrl = (itemId: string, o: { label?: string; download?: boolean } = {}) =>
  `/api/labels/product/${itemId}${query({ label: o.label, download: o.download ? "1" : undefined })}`;
const query = (params: Record<string, string | undefined>) => {
  const q = new URLSearchParams(Object.entries(params).filter((e): e is [string, string] => !!e[1])).toString();
  return q ? `?${q}` : "";
};

// Chips ------------------------------------------------------------------------------------------------

type Files = ReturnType<typeof filesOf>;
const chip = "inline-flex h-5 items-center rounded px-1.5 text-[11px] whitespace-nowrap";

/** One file a part needs: ✓ in the released revision, "not released" when only the draft has it, warn when missing. */
function NeedChip({ need, f }: { need: FileNeed; f: Files }) {
  if (f.released && need.kinds.some((k) => f.released?.has(k)))
    return (
      <span className={cx(chip, "bg-ok-soft text-ok")} title={`In Rev ${f.rev}: vendors get it with each PO`}>
        ✓ {need.label}
      </span>
    );
  if (need.kinds.some((k) => f.kinds.has(k)))
    return (
      <span className={cx(chip, "bg-accent-soft text-accent-text")} title="Uploaded to the draft spec; vendors get it once the spec is released">
        {need.label} · not released
      </span>
    );
  if (need.optional) return <span className={cx(chip, "bg-surface-2 text-faint")}>{need.label} (suggested)</span>;
  return (
    <span className={cx(chip, "bg-warn-soft text-warn")} title="Missing: upload it to the part's spec">
      {need.label}
    </span>
  );
}

/**
 * A part's files as chips (filesOf()): each file it needs with its state; packaging printed with text, whether its spec
 * (the words) is released; an HHL product label, its size; "No files needed" for plain and labelled packaging; a
 * packaging part without its Category asks for it (`onKind`).
 */
export function NeedChips({
  item,
  f,
  onKind,
  className,
}: {
  item: Pick<Item, "part_type"> & Partial<Pick<Item, "label_w_mm" | "label_h_mm">>;
  f: Files;
  onKind?: () => void;
  className?: string;
}) {
  const { pack, needs } = f;
  if (pack && !pack.type)
    return (
      <button type="button" onClick={onKind} className={cx(chip, "bg-warn-soft font-medium text-warn", onKind && "hover:underline")} title={pack.says}>
        Set its Category
      </button>
    );
  // Product labels --- no file to have: its size is what it needs.
  if (pack?.print === "template") {
    const size = labelSize(item);
    return size ? (
      <span className={cx(chip, "bg-ok-soft text-ok")} title={pack.says}>
        HHL label · {sizeText(size)}
      </span>
    ) : (
      <span className={cx(chip, "bg-warn-soft text-warn")} title="Set the label's size in its Packaging section">
        Label size not set
      </span>
    );
  }
  if (needs.files.length)
    return (
      <span className={cx("flex gap-1", className)}>
        {needs.files.map((need) => (
          <NeedChip key={need.label} need={need} f={f} />
        ))}
      </span>
    );
  if (pack?.print === "text" && pack.spec) {
    const title = "The words to print are in its spec's text";
    if (f.rev)
      return (
        <span className={cx(chip, "bg-ok-soft text-ok")} title={`${title} (Rev ${f.rev})`}>
          ✓ Text to print
        </span>
      );
    return f.spec ? (
      <span className={cx(chip, "bg-accent-soft text-accent-text")} title={`${title}; vendors get it once the spec is released`}>
        Text to print · not released
      </span>
    ) : (
      <span className={cx(chip, "bg-warn-soft text-warn")} title="Write the words to print in its spec">
        Text to print
      </span>
    );
  }
  if (pack || !needs.spec)
    return (
      <span className="text-[11px] text-faint" title={pack?.says}>
        No files needed
      </span>
    );
  return <span className="text-[11px] text-faint">{item.part_type === "other" ? "Set its Category to know" : "Nothing required"}</span>;
}

// The item panel ---------------------------------------------------------------------------------------

/** Where one print file stands: in the released revision, only uploaded to the draft, or missing. */
type NeedState = { need: FileNeed; state: "released" | "draft" | "missing"; slot?: SpecFile; version?: FileVersion; newer: boolean };

export function PackagingSection({ item }: { item: Item }) {
  const paneOpen = usePaneOpen(); // Side pane --- a new label part opens beside it
  const ix = useSpecIndex();
  const uploads = useUploads();
  const [releasing, setReleasing] = useState(false);
  const [newLabel, setNewLabel] = useState(false);
  // Part groups --- a file dropped on a kind the part's group shares (its dieline) can go to every part of the group
  const { byId, membersByGroup } = usePartGroups();
  const group = item.part_group_id ? byId.get(item.part_group_id) : undefined;
  const siblings = (group && membersByGroup.get(group.id)?.filter((m) => isCurrent(m) && m.id !== item.id)) || [];
  const [asking, setAsking] = useState<{ row: NeedState; file: File } | null>(null);
  const pack = packagingOf(item);
  if (!pack) return null;

  const editable = can("item.update");
  const canUpload = can("spec_file.create");
  const spec = ix.specByItem.get(item.id);
  const rev = spec?.current_revision_id ? ix.revisionById.get(spec.current_revision_id) : undefined;
  const revFiles = rev ? (ix.filesByRevision.get(rev.id) ?? []) : [];
  const slots = spec ? (ix.filesBySpec.get(spec.id) ?? []) : [];
  const status = statusOf(ix, spec);

  const rows: NeedState[] = pack.files.map((need) => {
    const inRev = revFiles.find((f) => (need.kinds as readonly string[]).includes(f.kind));
    const slot = needSlot(slots, need.kinds);
    const current = slot?.in_draft && slot.current_version_id ? ix.versionById.get(slot.current_version_id) : undefined;
    return {
      need,
      state: inRev ? "released" : current ? "draft" : "missing",
      slot,
      version: current ?? (inRev ? ix.versionById.get(inRev.file_version_id) : undefined),
      newer: !!inRev && !!current && current.id !== inRev.file_version_id,
    };
  });
  const words = pack.print === "text" && pack.spec;
  // Product labels --- files its released revision kept from before it was an HHL product label (its artwork).
  const leftover = pack.print === "template" && rev ? leftoverFiles(revFiles.map((f) => f.kind)) : null;
  const unreleased = rows.some((r) => r.state === "draft" || r.newer) || (words && (status.kind === "draft" || status.kind === "ahead"));

  /** The file goes into the slot of its kind (made if the spec hasn't one yet) as its newest upload (and, `all`, the group's). */
  const upload = async (row: NeedState, file: File, all = false) => {
    try {
      const r = await uploadNeed(ix, item.id, row.need, file);
      if (!r) return;
      const { id, unchanged } = r;
      if (all && siblings.length) {
        // Part groups --- the same upload into every other part's spec (stored once; each releases its own revision)
        const { ok, result } = await mutateWithResult("spec_file.copy_to", { file_version_id: id, item_ids: siblings.map((s) => s.id) });
        const r = result as { copied: unknown[] } | undefined;
        if (ok)
          toast.ok(
            `${file.name}: the ${row.need.label.toLowerCase()} of all ${siblings.length + 1} parts of ${group?.name}${r && r.copied.length < siblings.length ? ` (${siblings.length - r.copied.length} had it already)` : ""}`,
          );
      } else if (unchanged) toast.info(`${file.name} is the same as the current upload; nothing changed`);
      else toast.ok(`Uploaded ${file.name}`);
    } catch (e) {
      toast.error((e as Error).message);
    }
  };
  /** Part groups --- a kind the group shares asks first: every part (the default) or only this one. */
  const drop = (row: NeedState, file: File) => {
    const shared = !!group && siblings.length > 0 && row.need.kinds.some((k) => group.shared_files.includes(k));
    if (shared) setAsking({ row, file });
    else upload(row, file);
  };

  const setCategory = (t: PackType) => mutate("item.update", { id: item.id, patch: packCategoryPatch(item, t) });
  const writeWords = async () => {
    if (!spec && !(await specFor(ix, item.id))) return;
    requestAnimationFrame(toSpec);
  };

  const header = (
    <div className="flex items-center gap-2 px-2 pb-1">
      <h3 className="text-[11px] font-medium tracking-wide text-faint uppercase">Packaging</h3>
      {pack.type && <Badge tone="accent">{pack.title}</Badge>}
    </div>
  );

  // Its Category first: that decides everything else (afterwards the panel's Category field changes it).
  if (!pack.type)
    return (
      <section className="mt-3 border-t border-line pt-2">
        {header}
        <div className="mx-2 rounded-md border border-warn/40 bg-warn-soft/40 px-2.5 py-2">
          <div className="font-medium">What's its Category?</div>
          <div className="mb-2 text-[12px] text-muted">Its Category and how it's printed decide the files a vendor needs to make it.</div>
          <CategoryGrid value="" onPick={setCategory} disabled={!editable} />
        </div>
      </section>
    );

  // The printing its Category can have (and what it has, if that was set some other way).
  const usual = PACK_PRINT_OPTIONS[pack.type];
  const prints = usual.includes(pack.print) ? usual : [...usual, pack.print];
  return (
    <section className="mt-3 border-t border-line pt-2">
      {header}
      <Row label="Printing">
        {prints.length > 1 ? (
          <Select
            value={pack.print}
            options={prints.map((p) => ({ value: p, label: printLabel(pack.type, p) }))}
            disabled={!editable}
            onChange={(e) => {
              const print = e.target.value as PackPrint;
              // Product labels --- one without a size starts at 100 × 150 mm (the server does too; this saves the flash).
              const size = print === "template" && !labelSize(item) ? { label_w_mm: DEFAULT_LABEL_SIZE.w, label_h_mm: DEFAULT_LABEL_SIZE.h } : {};
              mutate("item.update", { id: item.id, patch: { pack_print: print, ...size } });
            }}
            className="border-transparent bg-transparent hover:border-line"
          />
        ) : (
          <span className="block px-2.5 pt-1.5 pb-1">{printLabel(pack.type, pack.print)}</span>
        )}
      </Row>
      <Row label="">
        <span className="block px-2.5 pb-1 text-[12px] text-muted">{pack.says}.</span>
      </Row>

      {pack.print === "template" && <ProductLabelPart item={item} editable={editable} />}
      {leftover?.text && rev && (
        <Note
          icon={<AlertTriangle className="size-4 text-warn" />}
          title={`Rev ${rev.rev} still has its ${leftover.text}`}
          says={`Vendors no longer get ${leftover.many ? "them" : "it"} with their POs: they get the labels HHL Ops makes from each product.`}
        />
      )}

      {rows.length > 0 && (
        <>
          <div className="mt-1 flex items-baseline gap-2 px-2 pb-1">
            <span className="text-[12px] font-medium">Print files</span>
            <span className="truncate text-[11px] text-faint">Vendors get the released revision with each PO</span>
          </div>
          <div className="flex flex-col gap-1 px-2">
            {rows.map((r) => (
              <NeedRow
                key={r.need.label}
                row={r}
                rev={rev?.rev ?? null}
                canUpload={canUpload}
                progress={r.slot ? uploads.get(r.slot.id) : undefined}
                onFile={(file) => drop(r, file)}
              />
            ))}
          </div>
        </>
      )}

      {words && (
        <Note
          title="Words to print"
          says="They go in its spec's text, which vendors get with each PO once it's released."
          state={
            <>
              <Badge tone={status.kind === "none" ? "warn" : status.kind === "draft" ? "accent" : "ok"}>
                {status.kind === "none" ? "No spec yet" : status.kind === "draft" ? "Not released yet" : `Rev ${rev?.rev} ✓`}
              </Badge>
              {status.kind === "ahead" && <Badge tone="accent">Changes not released</Badge>}
            </>
          }
          action={
            (spec || can("spec.create")) && (
              <Button size="sm" variant="ghost" onClick={writeWords}>
                {spec ? "Go to its spec" : "Start its spec"}
              </Button>
            )
          }
        />
      )}

      {pack.print === "label" && (
        <Note
          icon={<Tag className="size-4 text-faint" />}
          title="Its label"
          says={
            <>
              A label part (kind Label) holds the label's artwork; both go in the BOM. For an HHL product label, choose "Plain, with an HHL product label" above
              instead: one part.{" "}
              <Link to={LABELS_PATH} className="text-accent-text hover:underline">
                Labels in the library
              </Link>
            </>
          }
          action={
            can("item.create") && (
              <Button size="sm" icon={<Plus className="size-3.5" />} onClick={() => setNewLabel(true)}>
                New label part
              </Button>
            )
          }
        />
      )}

      {!pack.spec && pack.print !== "template" && <div className="px-4 pt-1 pb-1 text-[12px] text-faint">No spec needed: a PO sends just the part.</div>}

      {unreleased && (
        <div className="mx-2 mt-1.5 flex items-center gap-2 rounded-md bg-accent-soft/60 px-2.5 py-1.5 text-[12px]">
          <span className="min-w-0 flex-1">
            Vendors only get released files. {rev ? `POs send Rev ${rev.rev} until the spec is released again` : "Release the spec so POs send these"}
            {can("spec.release") ? "." : ": an admin releases it from the Spec section."}
          </span>
          {spec && can("spec.release") ? (
            <Button size="sm" variant="primary" icon={<Rocket className="size-3.5" />} onClick={() => setReleasing(true)}>
              Release…
            </Button>
          ) : (
            <Button size="sm" variant="ghost" onClick={toSpec}>
              Go to the spec
            </Button>
          )}
        </div>
      )}

      {releasing && spec && <ReleaseDialog spec={spec} ix={ix} title={item.name} beforeRelease={async () => true} onClose={() => setReleasing(false)} />}
      {asking && group && (
        <ShareChoice
          group={group}
          count={siblings.length + 1}
          label={asking.row.need.label}
          file={asking.file.name}
          onPick={(all) => {
            const { row, file } = asking;
            setAsking(null);
            if (all !== null) upload(row, file, all);
          }}
        />
      )}
      {newLabel && (
        <NewPackagingDialog
          initial={{ type: "label", name: `${item.name} label`, sku: item.sku ? `${skuStem(item.sku)}-LBL` : undefined }}
          onClose={() => setNewLabel(false)}
          onCreated={(id) => {
            paneOpen("item", id);
            toast.info("Not on a BOM yet: it joins a project when one of its BOMs uses it");
          }}
        />
      )}
    </section>
  );
}

/** A label and its control, laid out like the panel's fields. */
function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start rounded-md px-2">
      <div className="w-32 shrink-0 pt-1.5 text-muted">{label}</div>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

function Note({ icon, title, says, state, action }: { icon?: ReactNode; title: string; says: ReactNode; state?: ReactNode; action?: ReactNode }) {
  return (
    <div className="mx-2 mt-1 flex items-center gap-2 rounded-md border border-line px-2.5 py-1.5">
      {icon}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="font-medium">{title}</span>
          {state}
        </div>
        <div className="text-[12px] text-muted">{says}</div>
      </div>
      {action}
    </div>
  );
}

/** One print file: its state, the current upload, and a drop zone with Browse (a new upload of that file). */
function NeedRow({
  row,
  rev,
  canUpload,
  progress,
  onFile,
}: {
  row: NeedState;
  rev: string | null;
  canUpload: boolean;
  progress?: Upload;
  onFile: (file: File) => void;
}) {
  // The shared file box's behaviour (ui/FileDrop.tsx) on the row: drop the file on it, paste it, or Browse
  const drop = useFileDrop({ onFiles: ([f]) => onFile(f), multiple: false, disabled: !canUpload });
  const v = row.version;
  return (
    <div
      tabIndex={canUpload ? -1 : undefined}
      className={cx(
        "rounded-md border focus:outline-none",
        row.state === "missing" ? "border-dashed border-line-strong" : "border-line",
        drop.over && "border-accent bg-accent-soft",
      )}
      {...drop.zone}
    >
      <div className="flex items-center gap-2 px-2.5 py-1.5">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-medium">{row.need.label}</span>
            {row.state === "released" ? (
              <Badge tone="ok">Rev {rev} ✓</Badge>
            ) : row.state === "draft" ? (
              <Badge tone="accent">Uploaded, not released yet</Badge>
            ) : (
              <Badge tone="warn">Missing</Badge>
            )}
            {row.newer && <Badge tone="accent">Newer upload not released</Badge>}
          </div>
          <div className="truncate text-[12px] text-muted">
            {v ? (
              `${v.filename} · ${bytes(v.size)} · ${ago(v.uploaded_at)}`
            ) : canUpload ? (
              <span className="text-faint">Drop the file here, or browse</span>
            ) : (
              <span className="text-faint">Nothing uploaded</span>
            )}
          </div>
        </div>
        {v && (
          <span className="flex shrink-0 items-center">
            {viewable(v.content_type) && (
              <a href={downloadUrl(v.id, true)} target="_blank" rel="noreferrer" className="rounded p-1 text-muted hover:bg-hover hover:text-text" title="View">
                <Eye className="size-3.5" />
              </a>
            )}
            <a
              href={downloadUrl(v.id)}
              download={v.filename}
              className="rounded p-1 text-muted hover:bg-hover hover:text-text"
              title={`Download ${v.filename}`}
            >
              <Download className="size-3.5" />
            </a>
          </span>
        )}
        {canUpload && (
          <Button size="sm" variant="ghost" icon={<UploadIcon className="size-3.5" />} onClick={drop.choose}>
            {v ? "Replace" : "Browse…"}
          </Button>
        )}
        {drop.picker}
      </div>
      {progress && (
        <div className="px-2.5 pb-2">
          <div className="mb-0.5 flex justify-between text-[11px] text-muted">
            <span className="truncate">Uploading {progress.name}</span>
            <span className="font-mono">{Math.floor((progress.loaded / Math.max(1, progress.total)) * 100)}%</span>
          </div>
          <div className="h-1.5 overflow-hidden rounded bg-surface-2">
            <div className="h-full bg-accent transition-[width]" style={{ width: `${(progress.loaded / Math.max(1, progress.total)) * 100}%` }} />
          </div>
        </div>
      )}
    </div>
  );
}

/** A packaging part's Category as buttons (the panel's Packaging section before it's set, and New packaging part). */
function CategoryGrid({ value, onPick, disabled }: { value: PackType | ""; onPick: (t: PackType) => void; disabled?: boolean }) {
  return (
    <div className="grid grid-cols-4 gap-1">
      {PACK_TYPES.map((t) => (
        <button
          key={t}
          type="button"
          disabled={disabled}
          onClick={() => onPick(t)}
          className={cx(
            "h-8 truncate rounded-md border px-1.5 text-[12px] disabled:opacity-50",
            value === t ? "border-accent bg-accent-soft font-medium text-accent-text" : "border-line bg-surface hover:border-line-strong hover:bg-hover",
          )}
        >
          {PACK_CATEGORY_LABELS[t]}
        </button>
      ))}
    </div>
  );
}

// Product labels ---------------------------------------------------------------------------------------

type MmSize = { w: number; h: number };
/** The most a preview takes, in px: a 100 × 150 mm label shows 200 × 300 beside its products, a wide one across. */
const PREVIEW = { w: 440, h: 300 };
/** The panel's own look for its selects and inputs: no box until hovered. */
const bare = "border-transparent bg-transparent hover:border-line";
const linkButton =
  "inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md border border-line bg-surface px-2 text-xs font-medium whitespace-nowrap hover:bg-hover";

/**
 * An HHL product label's own settings and its labels: the size and the line every label carries; the products it labels
 * (where its BOMs put it), each with what's off about its label in plain words (no UPC, a bad check digit, no country of
 * origin); the chosen product's label as the server draws it, at the label's shape; and every label as one PDF.
 */
function ProductLabelPart({ item, editable }: { item: Item; editable: boolean }) {
  const ix = useBomStructure(); // no stock: an inventory change doesn't walk the BOMs again
  const items = useRows("item"); // whole rows: labelContent() reads the barcode, country and titles
  const sentPos = useSentPosLabelling(item.id);
  const byId = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const labelled = useMemo(() => productsLabelled(ix, item.id), [ix, item.id]);
  const [chosen, setChosen] = useState<string | null>(null);
  const size = labelSize(item);
  const save = (patch: Partial<Pick<Item, "label_w_mm" | "label_h_mm" | "label_note">>) => mutate("item.update", { id: item.id, patch });

  // At the label's size (item's label_w_mm × label_h_mm): a barcode too narrow to scan on it is warned like the rest.
  const products = labelled.flatMap((p) => {
    const product = byId.get(p.id);
    return product ? [{ product, says: labelContent(product, item) }] : [];
  });
  const current = products.find((p) => p.product.id === chosen) ?? products[0];
  // What every product's label lacks is said once, above the list.
  const common = products.length > 1 ? products[0].says.warnings.filter((w) => products.every((p) => p.says.warnings.includes(w))) : [];
  // The address changes when the label part or the product does (their updated_at), so the preview reloads only then.
  const v = [item.updated_at, current?.product.updated_at]
    .filter(Boolean)
    .map((t) => Date.parse(t as string).toString(36))
    .join(".");
  const src = `${labelPartUrl(item.id, { item: current?.product.id, v })}#toolbar=0&navpanes=0&view=Fit`;
  const box = size && { width: `min(100%, ${Math.round(Math.min(PREVIEW.w, (PREVIEW.h * size.w) / size.h))}px)`, aspectRatio: `${size.w} / ${size.h}` };

  return (
    <>
      <Row label="Size">
        <SizePicker
          size={size}
          disabled={!editable}
          selectClass={bare}
          onSize={(s) => save({ ...(s.w !== undefined && { label_w_mm: s.w }), ...(s.h !== undefined && { label_h_mm: s.h }) })}
        />
        {!size && (
          <span className="block px-2.5 pt-1 pb-1">
            <Badge tone="warn">Label size not set</Badge>
          </span>
        )}
        {sentPos.length > 0 && (
          <span className="block px-2.5 pb-1 text-[12px] text-muted" title={sentPos.map((p) => p.number).join(", ")}>
            Changes the labels vendors download for {sentPos[0].number}
            {sentPos.length > 1 ? ` and ${sentPos.length - 1} more` : ""}: labels are made when they're downloaded.
          </span>
        )}
      </Row>
      <Row label="Line on every label">
        <Input
          key={item.label_note}
          defaultValue={item.label_note}
          maxLength={300}
          disabled={!editable}
          placeholder="e.g. Wireless controller for Switch, PC and GameCube"
          onBlur={(e) => {
            const note = e.target.value.trim();
            if (note !== item.label_note) save({ label_note: note });
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
            else if (e.key === "Escape") {
              e.stopPropagation();
              e.currentTarget.value = item.label_note;
              e.currentTarget.blur();
            }
          }}
          className={bare}
        />
        <span className="block px-2.5 pb-1 text-[12px] text-muted">Optional: printed under the name on each product's label.</span>
      </Row>

      <div className="mt-1 flex items-center gap-2 px-2 pb-1">
        <span className="text-[12px] font-medium">
          {products.length ? `Labels · ${products.length} ${products.length === 1 ? "product" : "products"}` : "Labels"}
        </span>
        <span className="min-w-0 flex-1 truncate text-[11px] text-faint">Each from its product: name, variant, SKU and UPC</span>
        {products.length > 0 && size && (
          <a href={labelPartUrl(item.id, { download: true })} className={linkButton} title={`Every product's label, a page each, at ${sizeText(size)}`}>
            <Download className="size-3.5" />
            Download all labels (PDF)
          </a>
        )}
      </div>
      {!products.length && (
        <div className="mx-2 mb-1.5 rounded-md border border-dashed border-line-strong px-2.5 py-1.5 text-[12px]">
          <span className="font-medium">Not on any product yet.</span>{" "}
          <span className="text-muted">Add it to a product's BOM: each product gets its own label. Below is a sample.</span>
        </div>
      )}
      {common.length > 0 && (
        <div className="mx-2 mb-1 flex flex-col gap-0.5 text-[12px] text-warn">
          {common.map((w) => (
            <span key={w} className="flex items-start gap-1">
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0" /> Every product: {w}
            </span>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-start gap-2 px-2">
        <div className="flex flex-col gap-1" style={box ? { width: box.width } : undefined}>
          {box ? (
            <div style={{ aspectRatio: box.aspectRatio }} className="relative w-full overflow-hidden rounded-md border border-line bg-white">
              <iframe key={src} src={src} title={current ? `${current.product.sku} label` : "Sample label"} className="absolute inset-0 size-full" />
              {!current && (
                <span className="pointer-events-none absolute top-1 left-1">
                  <Badge tone="accent">Sample</Badge>
                </span>
              )}
            </div>
          ) : (
            <div className="flex h-24 w-56 items-center justify-center rounded-md border border-dashed border-line-strong px-3 text-center text-[12px] text-faint">
              Set its size to see the label
            </div>
          )}
          {box && (
            <span className="flex flex-wrap items-center gap-x-2 text-[11px]">
              <a href={src.split("#")[0]} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-accent-text hover:underline">
                <ExternalLink className="size-3" /> Full size
              </a>
              {current && (
                <PaneLink
                  kind="item"
                  id={current.product.id}
                  to={itemPath(current.product) ?? ""}
                  className="truncate text-muted hover:text-accent-text hover:underline"
                  title="Open the product"
                >
                  Open {current.product.sku}
                </PaneLink>
              )}
            </span>
          )}
        </div>
        {products.length > 0 && (
          <div className="flex max-h-[320px] min-w-[190px] flex-1 flex-col gap-0.5 overflow-y-auto">
            {products.map(({ product, says }) => {
              const own = says.warnings.filter((w) => !common.includes(w));
              const on = product.id === current?.product.id;
              return (
                <div
                  key={product.id}
                  className={cx("flex items-start gap-1 rounded-md border pl-2", on ? "border-accent bg-accent-soft/50" : "border-transparent hover:bg-hover")}
                >
                  <button type="button" onClick={() => setChosen(product.id)} className="min-w-0 flex-1 py-1 text-left" title="Show its label">
                    <span className="flex min-w-0 items-baseline gap-1.5">
                      <span className="shrink-0 font-mono text-[12px]">{product.sku}</span>
                      <span className="truncate text-[12px] text-muted" title={[says.title, says.subtitle].filter(Boolean).join(" · ")}>
                        {says.subtitle || says.title}
                      </span>
                    </span>
                    {own.map((w) => (
                      <span key={w} className="block text-[11px] leading-4 text-warn">
                        {w}
                      </span>
                    ))}
                  </button>
                  {size && (
                    <a
                      href={labelPartUrl(item.id, { item: product.id, download: true })}
                      className="mt-0.5 rounded p-1 text-muted hover:bg-hover hover:text-text"
                      title={`Download ${product.sku}'s label (PDF)`}
                    >
                      <Download className="size-3.5" />
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Note
        icon={<Tag className="size-4 text-faint" />}
        title="Vendors get them themselves"
        says="Vendors who make these products download their labels, at this size, from the PO's documents page: nothing to send."
      />
    </>
  );
}

/**
 * A label's size: the one label size picker (ui/LabelSizeFields.tsx: width, height, unit, Rotate, then the common stock
 * that fills them; Mitch, 2026-11-04). It's saved once a field is left or a stock is picked; `onSize` gets the size (the
 * panel saves it, the new part dialog keeps it).
 */
function SizePicker({ size, onSize, disabled }: { size: MmSize | null; onSize: (s: Partial<MmSize>) => void; disabled?: boolean; selectClass?: string }) {
  return (
    <LabelSizeFields
      size={size}
      presets={labelPresets(null, "", sizeText)}
      onSize={onSize}
      disabled={disabled}
      onInvalid={() => toast.error("A label is 10 to 400 mm on each side")}
    />
  );
}

// New packaging part ------------------------------------------------------------------------------------

/**
 * Makes a packaging part (Type Packaging: a bought material, part_type packaging) with its Category and printing, each
 * printing choice saying what files it means, then opens it (`onCreated`).
 */
export function NewPackagingDialog({
  initial,
  onClose,
  onCreated,
}: {
  /** `sku`: how the text after the fixed PAK- starts ("GCU2-" from a project's Parts library: shared/part-sku.ts); a part
   * has no project, its BOMs bring it into one. */
  initial: { type?: string; sku?: string; name?: string };
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [type, setType] = useState<PackType | "">(isPackType(initial.type) ? initial.type : "");
  const [print, setPrint] = useState<PackPrint | "">(isPackType(initial.type) ? PACK_PRINT_OPTIONS[initial.type][0] : "");
  const [name, setName] = useState(initial.name ?? "");
  const [sku, setSku] = useState(restOf(initial.sku ?? "", PART_PREFIXES.packaging)); // Part SKUs --- the text after PAK-
  const [size, setSize] = useState<MmSize>(DEFAULT_LABEL_SIZE); // Product labels --- for an HHL product label
  const [busy, setBusy] = useState(false);

  const pick = (t: PackType) => {
    setType(t);
    setPrint((p) => (p && PACK_PRINT_OPTIONS[t].includes(p) ? p : PACK_PRINT_OPTIONS[t][0]));
  };
  const submit = async () => {
    if (!type) return toast.error("Choose its Category");
    setBusy(true);
    const id = crypto.randomUUID();
    const ok = await mutate("item.create", {
      id,
      kind: "material",
      name,
      sku: PART_PREFIXES.packaging + sku.trim(),
      procurement: "buy",
      part_type: "packaging",
      pack_type: type,
      pack_print: print,
      ...(print === "template" && { label_w_mm: size.w, label_h_mm: size.h }),
    });
    setBusy(false);
    if (!ok) return;
    onClose();
    onCreated(id);
  };

  return createPortal(
    <DialogLayer onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            onClose();
          }
        }}
        className="w-[460px] rounded-xl border border-line bg-surface p-4 shadow-pop"
      >
        <div className="mb-3 text-[15px] font-semibold">New packaging part</div>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <span className="text-[12px] text-muted">Category</span>
            <CategoryGrid value={type} onPick={pick} />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[12px] text-muted">Printing</span>
            {!type ? (
              <div className="rounded-md border border-dashed border-line px-2.5 py-2 text-[12px] text-faint">Choose its Category first.</div>
            ) : (
              PACK_PRINT_OPTIONS[type].map((p) => (
                <label
                  key={p}
                  className={cx(
                    "flex cursor-pointer items-start gap-2 rounded-md border px-2.5 py-1.5",
                    print === p ? "border-accent bg-accent-soft/50" : "border-line hover:bg-hover",
                  )}
                >
                  <input type="radio" name="pack_print" checked={print === p} onChange={() => setPrint(p)} className="mt-1 accent-[var(--accent)]" />
                  <span className="min-w-0">
                    <span className="block">{printLabel(type, p)}</span>
                    <span className="block text-[12px] text-muted">
                      {packagingOf({ part_type: "packaging", pack_type: type, pack_print: p, label_w_mm: size.w, label_h_mm: size.h })?.says}
                    </span>
                  </span>
                </label>
              ))
            )}
          </div>
          {print === "template" && (
            <div className="flex flex-col gap-1">
              <span className="text-[12px] text-muted">Label size</span>
              <SizePicker size={size} onSize={(s) => setSize((was) => ({ ...was, ...s }))} />
            </div>
          )}
          <label className="flex flex-col gap-1">
            <span className="text-[12px] text-muted">Name</span>
            <Input
              autoFocus
              value={name}
              placeholder={`e.g. GCU2 ${type && type !== "other" ? PACK_CATEGORY_LABELS[type].replace(/ \/.*/, "") : "Retail Box"}`}
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[12px] text-muted">SKU</span>
            <SkuInput prefix={PART_PREFIXES.packaging} value={sku} placeholder="e.g. GCU2-BOX" onChange={setSku} />
          </label>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={busy} kbd="↵">
            Create
          </Button>
        </div>
      </form>
    </DialogLayer>,
    document.body,
  );
}
